import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle, XCircle, MessageSquare, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin · Manual Payment Verification tab.
 * Lists all cheque / NEFT / RTGS submissions from buyers with actions:
 *   Approve · Reject · Request Clarification (placeholder).
 */
export default function AdminManualPaymentsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending_verification');
  const [notesById, setNotesById] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter ? { status: filter } : {};
      const { data } = await axios.get(`${API}/admin/manual-payments`, { withCredentials: true, params });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error('Failed to load manual payments');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const act = async (id, action) => {
    if (action === 'reject' && !(notesById[id] || '').trim()) {
      toast.error('Please enter a reason before rejecting.');
      return;
    }
    if (action === 'approve' && !window.confirm('Approve this payment and activate the buyer\'s membership? A GST invoice will be generated and emailed.')) return;
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/manual-payments/${id}/${action === 'clarify' ? 'request-clarification' : action}`, { notes: notesById[id] || '' }, { withCredentials: true });
      toast.success(
        action === 'approve' ? 'Payment approved. Membership activated + invoice emailed.'
          : action === 'reject' ? 'Payment rejected. Buyer notified.'
          : 'Clarification request recorded.'
      );
      setNotesById(prev => ({ ...prev, [id]: '' }));
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const statusBadge = (s) => {
    const map = {
      pending_verification: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-700',
      clarification_requested: 'bg-orange-100 text-orange-800',
    };
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[s] || 'bg-muted'}`}>{s.replace(/_/g, ' ').toUpperCase()}</span>;
  };

  return (
    <div data-testid="admin-manual-payments-tab">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-bold text-navy">Manual Payment Verification</h2>
          <p className="text-xs text-muted-foreground">Verify Cheque / NEFT / RTGS payments and activate buyer memberships.</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={e => setFilter(e.target.value)} className="px-2 py-1.5 rounded border border-input text-sm" data-testid="mp-status-filter">
            <option value="pending_verification">Pending Verification</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="clarification_requested">Clarification Requested</option>
            <option value="">All</option>
          </select>
          <button onClick={load} className="p-2 rounded border border-input" data-testid="mp-refresh"><RefreshCw className="h-4 w-4" /></button>
        </div>
      </div>

      {loading && <div className="text-center py-8 text-muted-foreground text-sm"><Loader2 className="h-5 w-5 mx-auto animate-spin" /> Loading…</div>}
      {!loading && items.length === 0 && (
        <div className="text-center py-8 text-muted-foreground text-sm" data-testid="mp-empty">No manual payments in this state.</div>
      )}
      {!loading && items.length > 0 && (
        <div className="space-y-3">
          {items.map(mp => (
            <div key={mp.id} className="bg-white border border-border rounded-lg p-4 shadow-sm" data-testid={`mp-row-${mp.id}`}>
              <div className="grid md:grid-cols-4 gap-3 mb-3 text-sm">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Buyer</div>
                  <div className="font-semibold text-navy">{mp.buyer_name || '—'}</div>
                  <div className="text-xs text-muted-foreground truncate">{mp.buyer_email}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Society</div>
                  <div className="font-medium">{mp.organization_name || '—'}</div>
                  <div className="text-xs text-muted-foreground">{mp.buyer_type}</div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Payment</div>
                  <div className="font-medium">{(mp.method || '').toUpperCase()}</div>
                  <div className="text-xs text-muted-foreground">Bank: {mp.bank_name}</div>
                  <div className="text-xs text-muted-foreground">Ref: <span className="font-mono">{mp.reference_number}</span></div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Amount / Date</div>
                  <div className="font-bold text-navy">₹{Number(mp.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                  <div className="text-xs text-muted-foreground">Txn: {mp.transaction_date}</div>
                  <div className="text-xs text-muted-foreground">Plan: {mp.plan_label}</div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-border">
                <div className="text-xs text-muted-foreground">
                  Submitted: {new Date(mp.submitted_at).toLocaleString()} · Attempt #{mp.submission_count || 1}
                </div>
                <div className="ml-auto">{statusBadge(mp.status)}</div>
              </div>

              {mp.remarks && <div className="mt-2 text-xs text-muted-foreground italic">Buyer remarks: {mp.remarks}</div>}
              {mp.rejection_reason && <div className="mt-2 text-xs text-red-700">Previous rejection reason: {mp.rejection_reason}</div>}
              {mp.invoice_number && <div className="mt-2 text-xs text-green-700 font-medium">Invoice: {mp.invoice_number}</div>}

              {(mp.status === 'pending_verification' || mp.status === 'clarification_requested') && (
                <div className="mt-3 pt-3 border-t border-border">
                  <textarea
                    value={notesById[mp.id] || ''}
                    onChange={e => setNotesById(prev => ({ ...prev, [mp.id]: e.target.value }))}
                    placeholder="Optional notes (mandatory when rejecting) — e.g. 'Verified against HDFC stmt 12-Feb' OR 'UTR not found'"
                    rows={2}
                    className="w-full px-2 py-1.5 rounded border border-input text-sm mb-2"
                    data-testid={`mp-notes-${mp.id}`}
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => act(mp.id, 'approve')}
                      disabled={busyId === mp.id}
                      className="px-3 py-1.5 rounded bg-green-600 hover:bg-green-700 text-white text-xs font-bold inline-flex items-center disabled:opacity-50"
                      data-testid={`mp-approve-${mp.id}`}
                    >
                      <CheckCircle className="h-3.5 w-3.5 mr-1" /> Approve
                    </button>
                    <button
                      onClick={() => act(mp.id, 'reject')}
                      disabled={busyId === mp.id}
                      className="px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white text-xs font-bold inline-flex items-center disabled:opacity-50"
                      data-testid={`mp-reject-${mp.id}`}
                    >
                      <XCircle className="h-3.5 w-3.5 mr-1" /> Reject
                    </button>
                    <button
                      onClick={() => act(mp.id, 'clarify')}
                      disabled={busyId === mp.id}
                      className="px-3 py-1.5 rounded border border-orange text-orange text-xs font-bold inline-flex items-center disabled:opacity-50"
                      data-testid={`mp-clarify-${mp.id}`}
                    >
                      <MessageSquare className="h-3.5 w-3.5 mr-1" /> Request Clarification
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
